import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, getLocale, tLocation, tBusinessUnit } from '../i18n';
import api from '../utils/api';
import LunchCalendar from '../components/LunchCalendar';
import { useAuth } from '../context/AuthContext';

const STEPS = [
  t('onboardingStepGroupSize'),
  t('onboardingStepBusiness'),
  t('onboardingStepLocation'),
  t('onboardingStepLanguages'),
  t('onboardingStepAvailability')
];

const GROUP_OPTIONS = [
  { size: 2, label: t('groupSize2Label'), desc: t('groupSize2Desc'), heads: 2 },
  { size: 3, label: t('groupSize3Label'), desc: t('groupSize3Desc'), heads: 3 },
  { size: 4, label: t('groupSize4Label'), desc: t('groupSize4Desc'), heads: 4 }
];

function HeadIcons({ count, active }) {
  const color = active ? 'var(--match-a)' : '#9ca3af';
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
      {Array.from({ length: count }).map((_, i) => (
        <svg key={i} width="22" height="22" viewBox="0 0 24 24" fill={color}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
        </svg>
      ))}
    </div>
  );
}

export default function OnboardingPage() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const locale = getLocale();
  const [step, setStep] = useState(0);
  const [groupSizePreferences, setGroupSizePreferences] = useState([2]);
  const [allBusinessUnits, setAllBusinessUnits] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [allLanguages, setAllLanguages] = useState([]);
  const [myBusinessUnits, setMyBusinessUnits] = useState([]);
  const [interestedBusinessUnits, setInterestedBusinessUnits] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/profile/business-units/all').then(r => setAllBusinessUnits(r.data));
    api.get('/profile/locations/all').then(r => setAllLocations(r.data));
    api.get('/profile/languages/all').then(r => setAllLanguages(r.data));
  }, []);

  const selectMyBU = (id) => {
    setMyBusinessUnits(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleInterestedBU = (id) => {
    setInterestedBusinessUnits(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleLocation = (id) => {
    setSelectedLocations(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleLanguage = (id) => {
    setSelectedLanguages(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const finish = async () => {
    setSaving(true);
    try {
      await api.put('/profile/group-size-preference', { groupSizes: groupSizePreferences });
      await api.put('/profile/my-business-unit', { businessUnitIds: myBusinessUnits });
      await api.put('/profile/interested-business-units', { businessUnitIds: interestedBusinessUnits });
      await api.put('/profile/locations', { locationIds: selectedLocations });
      await api.put('/profile/languages', { languageIds: selectedLanguages });
      await api.put('/profile/availability', { dates: availability });
      await refreshUser();
      navigate('/dashboard');
    } catch {
      alert('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="onboarding-container" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f0fdf4, #fafdf5)', padding: '40px 20px' }}>
      <div style={{ maxWidth: 680, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>🍵</div>
          <h1 style={{ fontSize: 30, marginBottom: 8 }}>{t('onboardingHeader')}</h1>
          <p style={{ color: 'var(--text-muted)' }}>{t('onboardingSubheader')}</p>
        </div>

        {/* Step indicators */}
        <div className="step-indicators" style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 32 }}>
          {STEPS.map((s, i) => (
            <div key={s} className="step-indicator" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
                background: i <= step ? 'var(--match-a)' : 'var(--green-100)',
                color: i <= step ? 'white' : 'var(--text-muted)',
                transition: 'all 0.2s'
              }}>{i < step ? '✓' : i + 1}</div>
              <span style={{ fontSize: 14, fontWeight: i === step ? 600 : 400, color: i === step ? 'var(--match-a)' : 'var(--text-muted)' }}>{s}</span>
              {i < STEPS.length - 1 && <div className="step-connector" style={{ width: 40, height: 2, background: i < step ? 'var(--match-a)' : 'var(--border)' }} />}
            </div>
          ))}
        </div>

        <div className="card onboarding-card" style={{ padding: 36 }}>
          {/* Step 0: Group Size */}
          {step === 0 && (
            <div className="fade-up">
              <h2 style={{ marginBottom: 6 }}>{t('onboardingQuestionGroupSize')}</h2>
              <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 15 }}>
                {t('profileMatchLocation')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {GROUP_OPTIONS.map(opt => {
                  const active = groupSizePreferences.includes(opt.size);
                  return (
                    <div
                      key={opt.size}
                      onClick={() => setGroupSizePreferences(prev =>
                        prev.includes(opt.size)
                          ? prev.length > 1 ? prev.filter(s => s !== opt.size) : prev
                          : [...prev, opt.size]
                      )}
                      style={{
                        border: `2px solid ${active ? 'var(--match-a)' : 'var(--border)'}`,
                        borderRadius: 12,
                        padding: '16px 20px',
                        cursor: 'pointer',
                        background: active ? 'var(--green-50)' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        transition: 'all 0.15s'
                      }}
                    >
                      <HeadIcons count={opt.heads} active={active} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 16, color: active ? 'var(--match-a)' : 'inherit' }}>{opt.label}</div>
                        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{opt.desc}</div>
                      </div>
                      {active && (
                        <div style={{ marginLeft: 'auto', color: 'var(--match-a)', fontWeight: 700, fontSize: 18 }}>✓</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 1: Business Units */}
          {step === 1 && (
            <div className="fade-up">
              <div style={{ marginBottom: 32 }}>
                <h2 style={{ marginBottom: 6 }}>{t('onboardingQuestionBusiness')}</h2>
                <p style={{ color: 'var(--text-muted)', marginBottom: 16, fontSize: 15 }}>
                  {t('profileSelectApplies')}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {allBusinessUnits.map(bu => (
                    <div
                      key={bu.id}
                      className={`chip ${myBusinessUnits.includes(bu.id) ? 'selected' : ''}`}
                      onClick={() => selectMyBU(bu.id)}
                    >
                      {tBusinessUnit(bu.name)}
                    </div>
                  ))}
                </div>
              </div>

              <div>
              <h2 style={{ marginBottom: 6 }}>{t('onboardingQuestionInterested')}</h2>
              <p style={{ color: 'var(--text-muted)', marginBottom: 16, fontSize: 15 }}>
                {t('profileMatchLocation')}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <div
                    className={`chip ${interestedBusinessUnits.length === allBusinessUnits.length ? 'selected' : ''}`}
                    onClick={() => {
                      if (interestedBusinessUnits.length === allBusinessUnits.length) {
                        setInterestedBusinessUnits([]);
                      } else {
                        setInterestedBusinessUnits(allBusinessUnits.map(bu => bu.id));
                      }
                    }}
                  >
                    {t('anyLabel')}
                  </div>
                  {allBusinessUnits.map(bu => (
                    <div
                      key={bu.id}
                      className={`chip ${interestedBusinessUnits.includes(bu.id) ? 'selected' : ''}`}
                      onClick={() => toggleInterestedBU(bu.id)}
                    >
                      {tBusinessUnit(bu.name)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Location */}
          {step === 2 && (
            <div className="fade-up">
              <h2 style={{ marginBottom: 6 }}>{t('onboardingQuestionLocation')}</h2>
              <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 15 }}>
                {t('profileMatchLocation')}
              </p>
              {Object.entries(
                allLocations.reduce((groups, l) => {
                  (groups[l.country] = groups[l.country] || []).push(l);
                  return groups;
                }, {})
              ).map(([region, locations]) => (
                <div key={region} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{tLocation(region)}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {locations.map(l => (
                      <div
                        key={l.id}
                        className={`chip ${selectedLocations.includes(l.id) ? 'selected' : ''}`}
                        onClick={() => toggleLocation(l.id)}
                      >
                        {tLocation(l.name)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Step 3: Languages */}
          {step === 3 && (
            <div className="fade-up">
              <h2 style={{ marginBottom: 6 }}>{t('onboardingQuestionLanguages')}</h2>
              <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 15 }}>
                {t('profileLanguagesSpeak')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {allLanguages.map(l => (
                  <div
                    key={l.id}
                    className={`chip ${selectedLanguages.includes(l.id) ? 'selected' : ''}`}
                    onClick={() => toggleLanguage(l.id)}
                  >
                    {l.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Availability */}
          {step === 4 && (
            <div className="fade-up">
              <h2 style={{ marginBottom: 6 }}>{t('onboardingQuestionAvailability')}</h2>
              <p style={{ color: 'var(--text-muted)', marginBottom: 24, fontSize: 15 }}>
                {t('onboardingQuestionAvailabilityDesc')}
              </p>
              <LunchCalendar selected={availability} onChange={setAvailability} />
            </div>
          )}

          {/* Navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32, gap: 12 }}>
            {step > 0 ? (
              <button className="btn btn-outline" onClick={() => setStep(s => s - 1)}>
                {t('onboardingBack')}
              </button>
            ) : <div />}

            {step < STEPS.length - 1 ? (
              <button className="btn btn-primary" onClick={() => setStep(s => s + 1)}>
                {t('onboardingNext')}
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={finish}
                disabled={saving}
                style={{ minWidth: 140 }}
              >
                {saving ? t('onboardingSaving') : t('onboardingFinish')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
